// src/pages/monitoring/progress/SegmentProgressDetail.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Package, CheckCircle, Clock, XCircle, Edit } from 'lucide-react';
import toast from 'react-hot-toast';
import milestoneService from '../../../services/milestoneService';
import { getRAGStatusBadgeClass, formatProgress } from '../../../services/projectProgressService';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Segment Progress Detail Component
 * 
 * Shows:
 * 1. Segment profile card (green header)
 * 2. Milestone statistics cards (completed/in progress/not started)
 * 3. FLAT milestone list with indentation (hierarchical numbering)
 * 4. RAG status per milestone
 * 5. "Update" button → navigate to MilestoneProgressUpdate
 */
const SegmentProgressDetail = () => {
  const navigate = useNavigate();
  const { segmentId } = useParams();
  
  const [segment, setSegment] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (segmentId) {
      fetchSegmentDetail();
    }
  }, [segmentId]);

  const fetchSegmentDetail = async () => {
    setIsLoading(true);
    try {
      // Get milestone tree for segment
      const milestoneTree = await milestoneService.getMilestoneTreeBySegment(segmentId);
      
      // Flatten tree to list with levels
      const flatMilestones = flattenMilestoneTree(milestoneTree);
      setMilestones(flatMilestones);
      
      // Get segment info from first milestone
      if (flatMilestones.length > 0 && flatMilestones[0].foSegment) {
        setSegment(flatMilestones[0].foSegment);
      }
      
      console.log('✅ Segment detail loaded:', flatMilestones.length, 'milestones');
    } catch (error) {
      console.error('❌ Error loading segment detail:', error);
      toast.error('Failed to load segment details');
    } finally {
      setIsLoading(false);
    }
  };

  // Flatten tree to list with level and numbering
  const flattenMilestoneTree = (tree, level = 0, parentNumber = '') => {
    const result = [];
    
    tree.forEach((milestone, index) => {
      // Generate hierarchical number
      const number = parentNumber 
        ? `${parentNumber}.${index + 1}` 
        : `${index + 1}`;
      
      // Add milestone with metadata
      result.push({
        ...milestone,
        level,
        number,
        indent: level * 24 // 24px per level
      });
      
      // Recursively add children
      if (milestone.children && milestone.children.length > 0) {
        const childMilestones = flattenMilestoneTree(
          milestone.children, 
          level + 1, 
          number
        );
        result.push(...childMilestones);
      }
    });
    
    return result;
  };

  const handleUpdateProgress = (milestone) => {
    navigate(`/monitoring/progress/milestone/${milestone.id}`);
  };

  // Calculate statistics
  const stats = {
    total: milestones.length,
    completed: milestones.filter(m => m.status === 'COMPLETED').length,
    inProgress: milestones.filter(m => m.status === 'IN_PROGRESS').length,
    notStarted: milestones.filter(m => m.status === 'NOT_STARTED').length
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex space-x-2">
          <div className="w-3 h-3 bg-green-400 rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-3 h-3 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">FO Segment Progress Details</h1>
            <p className="text-gray-600 mt-1">{segment?.spanId || 'Loading...'}</p>
          </div>
        </div>
      </div>

      {/* Segment Profile Card */}
      {segment && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-lg">
            <div className="flex items-center gap-2 mb-4">
              <Package className="w-5 h-5" />
              <h3 className="text-lg font-bold">Segment Profile & Progress</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-green-200 mb-1">Span ID</label>
                <p className="text-sm font-bold">{segment.spanId}</p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-green-200 mb-1">Segment Name</label>
                <p className="text-sm font-semibold">{segment.siteNameAB}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-200 mb-1">Progress</label>
                <p className="text-2xl font-bold">{formatProgress(segment.progressPercentage)}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-200 mb-1">Main Area</label>
                <p className="text-sm font-semibold">{segment.mainArea}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-200 mb-1">Location</label>
                <p className="text-sm">{segment.kota}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-200 mb-1">Length</label>
                <p className="text-sm font-semibold">{segment.commercialDistance} km</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Milestone Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Milestones</div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
              <div className="text-sm text-green-600">Completed</div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-700">{stats.inProgress}</div>
              <div className="text-sm text-yellow-600">In Progress</div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-400 rounded-full flex items-center justify-center">
              <XCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-700">{stats.notStarted}</div>
              <div className="text-sm text-gray-600">Not Started</div>
            </div>
          </div>
        </div>
      </div>

      {/* FLAT Milestone List */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Milestone List</h3>
          
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-green-600 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">No</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Milestone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Evidence</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Finish Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Progress</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">RAG</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {milestones.map((milestone) => (
                  <tr 
                    key={milestone.id} 
                    className={`hover:bg-green-50 transition-colors ${
                      milestone.isGroup ? 'bg-yellow-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {milestone.number}
                    </td>
                    <td className="px-4 py-3" style={{ paddingLeft: `${16 + milestone.indent}px` }}>
                      <div className="flex items-center gap-2">
                        {milestone.isGroup && (
                          <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs rounded-full font-semibold">
                            GROUP
                          </span>
                        )}
                        <span className={`text-sm ${milestone.isGroup ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                          {milestone.milestoneName}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {milestone.itemCode}
                        {milestone.level > 0 && ` • Level ${milestone.level}`}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {milestone.evidenceCount || 0} / {milestone.requiredEvidenceCount || 0}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {milestone.planStartDate ? new Date(milestone.planStartDate).toLocaleDateString('id-ID') : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {milestone.planFinishDate ? new Date(milestone.planFinishDate).toLocaleDateString('id-ID') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
                          <div 
                            className="bg-green-600 rounded-full h-2 transition-all"
                            style={{ width: `${Math.min(milestone.finalProgress || 0, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700 min-w-[40px]">
                          {formatProgress(milestone.finalProgress)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`w-8 h-8 rounded-full ${getRAGStatusBadgeClass(milestone.ragStatus)}`}></div>
                    </td>
                    <td className="px-4 py-3">
                      {!milestone.isGroup && (
                        <button
                          onClick={() => handleUpdateProgress(milestone)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm shadow-md hover:shadow-lg transition-all"
                        >
                          <Edit className="w-4 h-4 inline mr-1" />
                          Update
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SegmentProgressDetail;